// Columna derecha de Tiendas: encabezado de la sucursal y pestañas Desempeño, Equipo y Ventas.
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, Moon, Store, Target, TrendingDown } from 'lucide-react';
import { StoreStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/StoreStatusEnum';
import { Button, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { DetailCard, DetailTabs } from '@/shared/patterns/DetailCard';
import { UnavailableState } from '@/shared/patterns/DataTable';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { NoSourceBlock } from '@/shared/patterns/NoSource';
import { StatusPill } from '@/shared/patterns/Pills';
import { Sparkline } from '@/shared/patterns/Sparkline';
import {
    reportPeriodDays,
    reportPeriodLabel,
    type ReportPeriod,
} from '@/shared/patterns/PeriodBand';
import { buildCategoryMix, type CategoryMixSlice } from '../lib/categoryMix';
import {
    storeBenchmarks,
    trendChangePct,
    uniqueCustomers,
    versusNetwork,
    type StoreBenchmark,
} from '../lib/storeDetail';
import {
    daysWithoutSale,
    storeAlerts,
    storeKindLabel,
    type StoreContext,
    type StoreRow,
} from '../lib/storesTable';
import { money } from '../lib/tiendasFormat';
import { CategoryIcon } from '@/shared/patterns/SaleTicket';
import { RankMedal, noSaleText } from './StoreBadges';
import {
    CategoryCard,
    SalesUnavailable,
    StoreSalesTab,
    type StoreSalesState,
} from './StoreSalesTab';
import { StoreTeamTab, type StoreTeamState } from './StoreTeamTab';

export type StoreDetailTab = 'performance' | 'team' | 'sales';

export interface StoreTrendState {
    /** Colocación por semana, de la más vieja a la más nueva; `undefined` mientras carga. */
    series: number[] | undefined;
    isError: boolean;
}

function Header({ row, rank }: { row: StoreRow; rank: number | undefined }) {
    const sub: ReactNode[] = [
        row.code ? (
            <span key="code" className="font-mono">
                {row.code}
            </span>
        ) : null,
        row.activeSellers === null
            ? null
            : row.teamTotalCount !== null && row.teamTotalCount !== row.activeSellers
              ? `${String(row.activeSellers)} de ${String(row.teamTotalCount)} vendedores activos`
              : `${String(row.activeSellers)} ${row.activeSellers === 1 ? 'vendedor activo' : 'vendedores activos'}`,
        row.activeSellers === null
            ? null
            : row.managerId === null
              ? 'sin admin'
              : `admin: ${row.managerName ?? 'sin nombre en el padrón'}`,
    ].filter((part) => part !== null && part !== '');
    return (
        <header className="flex flex-none items-center gap-3 border-b border-border bg-card px-4 pb-3 pt-3.5">
            <div
                className="flex size-12 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary"
                aria-hidden
            >
                <Store className="size-6" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                    <RankMedal rank={rank} className="size-5" />
                    <h2 className="truncate text-[18px] font-bold leading-tight">{row.name}</h2>
                    {row.zoneName ? (
                        <span className="rounded border border-border bg-muted px-1.5 py-px text-[9.5px] font-semibold text-muted-foreground">
                            {row.zoneName}
                        </span>
                    ) : null}
                    {row.kind ? (
                        <StatusPill tone={row.kind === 'OWN' ? 'info' : 'neutral'}>
                            {storeKindLabel(row.kind)}
                        </StatusPill>
                    ) : null}
                </div>
                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {sub.map((part, index) => (
                        <span key={index}>
                            {index > 0 ? ' · ' : null}
                            {part}
                        </span>
                    ))}
                </div>
            </div>
        </header>
    );
}

/** Aviso de una tienda suspendida o dada de baja; con cadena integrada dice que la mueve Zentral. */
export function StoreStatusNotice({ row, integrated }: { row: StoreRow; integrated: boolean }) {
    const { t } = useTranslation('stores');
    if (row.status !== StoreStatusEnum.SUSPENDED && row.status !== StoreStatusEnum.INACTIVE) {
        return null;
    }
    const title = t(`notice.${row.status}.${integrated ? 'integrated' : 'plain'}`);
    return (
        <div
            role="status"
            className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px]"
        >
            <Ban className="mt-[1px] size-4 flex-none text-warning" aria-hidden />
            <div>
                <p className="font-semibold">{title}</p>
                <p>{t('notice.impact')}</p>
                {integrated ? (
                    <p className="text-muted-foreground">{t('notice.reactivates')}</p>
                ) : null}
            </div>
        </div>
    );
}

function StatChip({
    label,
    wide = false,
    children,
}: {
    label: string;
    wide?: boolean;
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                'flex min-w-0 flex-col items-center rounded-md border border-border bg-card px-2 py-1',
                wide ? 'flex-[1.6]' : 'flex-1',
            )}
        >
            <div className="w-full text-center text-[8.5px] font-bold uppercase leading-tight tracking-[0.02em] text-muted-foreground">
                {label}
            </div>
            <div className="mt-0.5 whitespace-nowrap text-[13px] font-bold tabular-nums">
                {children}
            </div>
        </div>
    );
}

const DASH = <span className="text-muted-foreground">—</span>;

function DaysWithoutSale({
    row,
    context,
    period,
}: {
    row: StoreRow;
    context: StoreContext;
    period: ReportPeriod;
}) {
    const days = daysWithoutSale(row, context.now);
    if (days === null) {
        const periodDays = reportPeriodDays(period);
        if (periodDays === null || (row.salesCount ?? 0) > 0) return DASH;
        return <span className="text-danger">&gt;{periodDays}d</span>;
    }
    const tone = days > 3 ? 'text-danger' : days > 1 ? 'text-warning' : 'text-success';
    return <span className={tone}>{days === 0 ? 'hoy' : `${String(days)}d`}</span>;
}

function ActiveAlert({
    tone,
    icon,
    children,
}: {
    tone: 'success' | 'warning';
    icon: ReactNode;
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                'mb-2 flex items-center gap-2.5 rounded-[5px] border-l-[3px] px-3.5 py-2.5 text-[11.5px] font-bold',
                tone === 'success'
                    ? 'border-success bg-success-soft text-success'
                    : 'border-warning bg-warning-soft text-warning',
            )}
        >
            {icon}
            {children}
        </div>
    );
}

function BenchmarkRow({
    label,
    benchmark,
    format,
}: {
    label: string;
    benchmark: StoreBenchmark;
    format: (value: number) => string;
}) {
    const show = (value: number | null) => (value === null ? '—' : format(value));
    const vs = versusNetwork(benchmark.value, benchmark.networkMedian);
    return (
        <div className="grid grid-cols-[80px_1fr_auto] items-center gap-2.5 border-b border-border/40 px-3 py-2 last:border-b-0">
            <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
            <span className="text-[15px] font-bold tabular-nums">{show(benchmark.value)}</span>
            <div className="flex items-center justify-end gap-3 text-[10.5px] tabular-nums text-muted-foreground">
                <span>mediana red {show(benchmark.networkMedian)}</span>
                <span
                    className={cn(
                        'rounded-[3px] px-1.5 py-0.5 text-[10px] font-bold',
                        vs.tone === 'good' && 'bg-success-soft text-success',
                        vs.tone === 'bad' && 'bg-danger-soft text-danger',
                        vs.tone === 'flat' && 'bg-muted text-muted-foreground',
                    )}
                >
                    {vs.label}
                </span>
            </div>
        </div>
    );
}

function TrendCard({ trend }: { trend: StoreTrendState }) {
    if (trend.isError) {
        return (
            <DetailCard title="Tendencia">
                <NoSourceBlock>No se pudo leer la serie semanal de colocación.</NoSourceBlock>
            </DetailCard>
        );
    }
    if (trend.series === undefined) {
        return (
            <DetailCard title="Tendencia">
                <Skeleton lines={2} />
            </DetailCard>
        );
    }
    const last = trend.series[trend.series.length - 1];
    const change = trendChangePct(trend.series);
    return (
        <DetailCard title="Tendencia">
            <div className="flex items-center gap-4">
                <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                    Últimas 8 sem
                </span>
                <Sparkline values={trend.series} label="Colocación de las últimas 8 semanas" />
                <div className="ml-auto text-right">
                    <div className="text-[10.5px] text-muted-foreground">Colocación</div>
                    <div className="text-[15px] font-bold tabular-nums">{money(last)}</div>
                    <div className="text-[10px] text-muted-foreground">
                        {change === null
                            ? '— vs 8 sem atrás'
                            : `${change > 0 ? '+' : ''}${String(change)}% vs 8 sem atrás`}
                    </div>
                </div>
            </div>
        </DetailCard>
    );
}

function PerformanceTab({
    row,
    rows,
    context,
    sales,
    trend,
    period,
    categoryMix,
}: {
    row: StoreRow;
    rows: readonly StoreRow[];
    context: StoreContext;
    sales: StoreSalesState;
    trend: StoreTrendState;
    period: ReportPeriod;
    categoryMix: CategoryMixSlice[] | undefined;
}) {
    const alerts = storeAlerts(row, context);
    const benchmarks = storeBenchmarks(row, rows);
    const rank = context.ranks.get(row.storeId);
    const mix = useMemo(() => {
        if (categoryMix) return categoryMix;
        return sales.items ? buildCategoryMix(sales.items, sales.legacyCategories) : null;
    }, [categoryMix, sales.items, sales.legacyCategories]);
    const customers = row.customersServed ?? (sales.items ? uniqueCustomers(sales.items) : null);
    const customersText =
        customers === null
            ? null
            : `${String(customers)}${row.customersServed === null && sales.truncated ? '+' : ''}`;
    const integer = (value: number) => String(Math.round(value));
    const pesos = (value: number) => money(Math.round(value));

    return (
        <>
            <div className="flex gap-1.5 border-b border-border bg-muted/40 px-3 py-2.5">
                <StatChip label="Ranking en la red">
                    {rank === undefined ? (
                        DASH
                    ) : (
                        <>
                            #{rank}{' '}
                            <span className="text-[10.5px] font-medium text-muted-foreground">
                                /{context.rankedCount}
                            </span>
                        </>
                    )}
                </StatChip>
                <StatChip label="Colocación" wide>
                    {money(row.placedCents)}
                </StatChip>
                <StatChip label="Vendedores activos">{row.activeSellers ?? DASH}</StatChip>
                <StatChip label="Clientes atendidos">{customersText ?? DASH}</StatChip>
                <StatChip label="Días s/venta">
                    <DaysWithoutSale row={row} context={context} period={period} />
                </StatChip>
            </div>

            <div className="p-3">
                {alerts.topNetwork ? (
                    <ActiveAlert tone="success" icon={<Target className="size-4" aria-hidden />}>
                        Top 10% de la red por colocación
                    </ActiveAlert>
                ) : null}
                {alerts.noSale !== null ? (
                    <ActiveAlert tone="warning" icon={<Moon className="size-4" aria-hidden />}>
                        {noSaleText(alerts.noSale)}
                    </ActiveAlert>
                ) : null}
                {alerts.drop ? (
                    <ActiveAlert
                        tone="warning"
                        icon={<TrendingDown className="size-4" aria-hidden />}
                    >
                        Colocación cayó más del 20% vs semana anterior
                    </ActiveAlert>
                ) : null}

                <DetailCard
                    title="Desempeño vs mediana de tu red"
                    meta={reportPeriodLabel(period)}
                    flush
                >
                    <BenchmarkRow label="Ventas" benchmark={benchmarks.sales} format={integer} />
                    <BenchmarkRow label="Colocación" benchmark={benchmarks.placed} format={pesos} />
                    <BenchmarkRow
                        label="Ticket prom"
                        benchmark={benchmarks.averageTicket}
                        format={pesos}
                    />
                    <div
                        className="grid grid-cols-[80px_1fr_auto] items-center gap-2.5 px-3 py-2"
                        title="El NPI por tienda todavía no tiene fuente"
                    >
                        <span className="text-[11px] font-semibold text-muted-foreground">
                            NPI M30
                        </span>
                        <span className="text-[15px] font-bold text-muted-foreground">—</span>
                        <span className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
                            <AlertTriangle className="size-3" aria-hidden />
                            umbral 5% · sin fuente
                        </span>
                    </div>
                </DetailCard>

                <TrendCard trend={trend} />

                <DetailCard
                    title="Mix por categoría"
                    meta={
                        customersText === null
                            ? undefined
                            : `${customersText} ${customers === 1 ? 'cliente atendido' : 'clientes atendidos'}`
                    }
                >
                    {mix === null ? (
                        <SalesUnavailable sales={sales} />
                    ) : mix.length === 0 ? (
                        <NoSourceBlock>Sin ventas registradas en el periodo.</NoSourceBlock>
                    ) : (
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(112px,1fr))] gap-2">
                            {mix.map((slice) => (
                                <CategoryCard
                                    key={slice.category}
                                    icon={<CategoryIcon category={slice.category} />}
                                    label={slice.label}
                                    salesCount={slice.salesCount}
                                    amountCents={slice.amountCents}
                                    unit={slice.unit}
                                />
                            ))}
                        </div>
                    )}
                </DetailCard>
            </div>
        </>
    );
}

export function StoreDetailPanel({
    row,
    rows,
    integrated = false,
    context,
    hasSelection,
    isLoading,
    error,
    onRetry,
    tab,
    onTabChange,
    team,
    sales,
    trend,
    period,
    onOpenSeller,
    categoryMix,
}: {
    /** La fila de la tienda elegida; `undefined` si no está en la lista. */
    row: StoreRow | undefined;
    rows: readonly StoreRow[];
    /** La cadena está integrada con Zentral: el aviso de estatus lo atribuye a Zentral. */
    integrated?: boolean;
    context: StoreContext;
    hasSelection: boolean;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    tab: StoreDetailTab;
    onTabChange: (tab: StoreDetailTab) => void;
    team: StoreTeamState;
    sales: StoreSalesState;
    trend: StoreTrendState;
    period: ReportPeriod;
    onOpenSeller: (sellerId: string) => void;
    /** El mix de la ficha del BFF; sin él se arma con las ventas de la tienda. */
    categoryMix?: CategoryMixSlice[];
}) {
    if (!hasSelection) {
        return (
            <MasterDetailEmpty
                icon={<Store className="size-8" aria-hidden />}
                title="Selecciona una tienda"
                sub="Elige una fila de la izquierda para ver su desempeño en esta unidad."
            />
        );
    }
    if (error) {
        return (
            <UnavailableState
                title="No se pudo cargar la tienda"
                body={error.message}
                action={
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        Reintentar
                    </Button>
                }
            />
        );
    }
    if (isLoading) {
        return (
            <div className="flex flex-col gap-3 p-3.5">
                <Skeleton className="h-5 w-48" />
                <Skeleton lines={7} />
            </div>
        );
    }
    if (!row) {
        return (
            <MasterDetailEmpty
                icon={<Store className="size-8" aria-hidden />}
                title="Esta tienda no está en la lista"
                sub="No aparece en el padrón ni en las ventas del periodo elegido."
            />
        );
    }

    const teamCount = team.members?.length;
    const salesCount = sales.items?.filter((sale) => sale.cancelledAt === null).length;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <Header row={row} rank={context.ranks.get(row.storeId)} />
            <div className="min-h-0 flex-1 overflow-y-auto">
                <StoreStatusNotice row={row} integrated={integrated} />
                <div className="bg-card pt-2">
                    <DetailTabs<StoreDetailTab>
                        tabs={[
                            { key: 'performance', label: 'Desempeño' },
                            {
                                key: 'team',
                                label:
                                    teamCount === undefined
                                        ? 'Equipo'
                                        : `Equipo ${String(teamCount)}`,
                            },
                            {
                                key: 'sales',
                                label:
                                    salesCount === undefined
                                        ? 'Ventas'
                                        : `Ventas ${String(salesCount)}`,
                            },
                        ]}
                        active={tab}
                        onChange={onTabChange}
                    />
                </div>
                {tab === 'performance' ? (
                    <PerformanceTab
                        row={row}
                        rows={rows}
                        context={context}
                        sales={sales}
                        trend={trend}
                        period={period}
                        categoryMix={categoryMix}
                    />
                ) : tab === 'team' ? (
                    <StoreTeamTab team={team} period={period} onOpenSeller={onOpenSeller} />
                ) : (
                    <StoreSalesTab key={row.storeId} sales={sales} />
                )}
            </div>
        </div>
    );
}
